//@flow
/**
 * NOTE:
 * This component replaces both Header and OfficeHeader
 * components as a single, unified header. The naming should
 * be updated to just `Header` and other components removed
 * once it is established that this will be the new header
 * for the long term.
 */
import React from 'react';
import { withRouter } from 'react-router';
import Link from '../../../components/Link';
import CategoriesDropdown from '../../Categories/components/CategoriesDropdown.react';
import AdaptogensDropdown from './AdaptogensDropdown.react';
import AccountDropdown from './AccountDropdown.react';
import OfficeSubPagesDropdown from './OfficeSubPagesDropdown.react';
import {
  GiHamburgerMenu,
} from 'react-icons/gi';
import CartActions from '../../Actions/cart';
import HelloBar from '../../HelloBar/components/HelloBar.react';
import AccessibleButtonWrapper from '../../Icon/components/AccessibleButtonWrapper.react';
import ShippingInfoUtil from '../../Utils/shippingInfoUtil';

import imgMembershipLogo from '../../../images/svg-icons/membership-logo-new.svg';
import imgMembershipLogoWhite from '../../../images/svg-icons/membership-logo-new-white.svg';

import type CategoriesCollection from '../../Stores/MemberCategories/categoriesCollection';
import type CustomerModel from '../../Stores/Customer/customerModel';
import type QuoteModel from '../../Stores/Quote/quoteModel';
import type QuoteItemsCollection from '../../Stores/Quote/quoteItemsCollection';
import type QuoteTotalsModel from '../../Stores/Quote/quoteTotalsModel';
import type { HelloBarConfig } from '../../Stores/Mktg/mktgModelInterfaces';
import { FaSearch } from 'react-icons/fa';
import ModalActions from '../../Actions/modal';
import Constants from '../../constants';
import MediaQuery from 'react-responsive';
import Query from '../../Utils/query';

import type {ContextRouter} from 'react-router';

type Props = ContextRouter&{
  openMenu:() => void,
  handleLoginClick:() => void,
  categories:CategoriesCollection,
  customer:CustomerModel,
  cartQty:number,
  disableCartPreview:boolean,
  showCategories:boolean,
  quoteItems:QuoteItemsCollection,
  quoteTotals:QuoteTotalsModel,
  inUS:boolean,
  quote:QuoteModel,
  hasSub:boolean,
  helloBarConfig:HelloBarConfig,
  newMember:boolean,
  helloBar:?boolean,
  simpleHeader:boolean,
  unstickMobile:boolean,
};

class Header extends React.Component<Props> {

  _getIsOfficeRoute() {
    const {location} = this.props;
    return location.pathname.startsWith('/office');
  }

  _getLogoLinkData() {
    const isOffice = this._getIsOfficeRoute();
    if (isOffice) {
      return {link: '/office', external: true};
    } else {
      return {link: '/', external: false};
    }
  }

  _getUserName() {
    if (this.props.simpleHeader) return null;

    if (this.props.customer.getId()) {
      const displayName = this.props.customer.getFirstname() || 'Account';
      return (
        <li role="menuitem" aria-haspopup="true" className="account-dropdown-item">
          <AccountDropdown
            label={displayName}
            hasSub={this.props.hasSub}
          />
        </li>
      );
    } else {
      return(
        <li role="menuitem" className="signin">
          <button onClick={this.props.handleLoginClick}>
            Sign in
          </button>
        </li>
      );
    }
  }

  _getCart() {
    const isOffice = this._getIsOfficeRoute();
    // Don't render cart preview on any office routes
    if (this.props.simpleHeader || isOffice) return null;

    const openCartPreview = this.props.disableCartPreview
      ? null
      : CartActions.openCartPreview;

    return (
      <AccessibleButtonWrapper
        className="cart-link-holder"
        label="cart"
        action={openCartPreview}
      >
        <div className="cart-icon">
          <span className="cart-quantity">
            {this.props.cartQty}
          </span>
        </div>
      </AccessibleButtonWrapper>
    );
  }

  _getMobileMenu() {
    if (this.props.simpleHeader) return null;
    return (
      <button
        className="header-hamburger-button"
        onClick={this.props.openMenu}
      >
        <GiHamburgerMenu
          className="header-hamburger-icon"
        />
      </button>
    );
  }

  _getHelloBar() {
    if (this.props.inUS && !ShippingInfoUtil.ipIsInCA() && this.props.helloBar) {
      return (
        <div className="header-bottom">
          <HelloBar
            quoteTotals={this.props.quoteTotals}
            inUS={this.props.inUS}
            config={this.props.helloBarConfig}
            newMember={this.props.newMember}
            customer={this.props.customer}
          />
        </div>
      );
    }
  }

  _getOrderAgainLink() {
    if (this.props.customer.hasMembershipOrders()) {
      return (
        <li role="menuitem">
          <Link to="/orders">
            Order again
          </Link>
        </li>
      );
    }
  }

  _getSearchIcon() {
    return (
      <li role="menuitem" className="search-button-container">
        <button className="search-button" onClick={this.openSearch}>
          <FaSearch
            className="search-icon"
          />
        </button>
      </li>
    );
  }

  openSearch = () => {
    ModalActions.openModal(Constants.SEARCH_MODAL);
  }

  renderOfficeItems() {
    return (
      <>
        <li>
          <Link
            to="/office/catalog"
            external={true}
          >
            Snack Catalog
          </Link>
        </li>
        <li>
          <Link
            to="/office/pricing"
            external={true}
          >
            Pricing
          </Link>
        </li>
        <li>
          <Link
            to="/office/pantry"
            external={true}
          >
            In-office
          </Link>
        </li>
        <li>
          <Link
            to="/office/remote"
            external={true}
          >
            Remote
          </Link>
        </li>
        {/* <li>
          <Link
            to="/office/podcast"
            external={true}
          >
            Podcast
          </Link>
        </li> */}
        <li
          role="menuitem"
          aria-haspopup="true"
        >
          <OfficeSubPagesDropdown />
        </li>
        <li>
          <Link
            to="/office?start=true"
            className="get-started"
            external={true}
          >
            Get Started
          </Link>
        </li>
      </>
    );
  }

  renderDtcItems() {
    return (
      <>
        <li
          role="menuitem"
          aria-haspopup="true"
          className="our-snacks"
        >
          <CategoriesDropdown
            items={this.props.categories.getDropdownCategories()}
          />
        </li>
        <li role="menuitem">
          <Link to="/products/m-snack-boxes">
            Snack Boxes
          </Link>
        </li>
        <li role="menuitem">
          <Link to="/aboutus" external={true}>
            About Us
          </Link>
        </li>
        <li
          role="menuitem"
          aria-haspopup="true"
          className="our-snacks"
        >
          <AdaptogensDropdown />
        </li>
        {this._getOrderAgainLink()}
      </>
    );
  }

  renderMiddleLeft() {
    if (this.props.simpleHeader || !this.props.showCategories) {
      return null;
    }
    const isOffice = this._getIsOfficeRoute();
    const items = isOffice
      ? this.renderOfficeItems()
      : this.renderDtcItems();

    return (
      <div className="header-links-container">
        <ul
          role="menubar"
          aria-hidden="false"
          className="header-links"
        >
          {items}
        </ul>
      </div>
    );
  }

  renderMiddleRight() {
    const isOffice = this._getIsOfficeRoute();
    return (
      <ul role="menubar" className="nb-nav nb-nav-right">
        {!isOffice && this._getSearchIcon()}
        {this._getUserName()}
        {!isOffice && this._getCart()}
      </ul>
    );
  }

  renderTabs() {
    const isOffice = this._getIsOfficeRoute();
    const tabs = [
      {
        to: '/',
        external: false,
        label: 'Home',
        className: `header-tab ${!isOffice ? 'active' : ''}`,
      },
      {
        to: '/office',
        external: true,
        label: 'Business',
        className: `header-tab ${isOffice ? 'active' : ''}`,
      },
    ];
    return (
      <div className="header-tabs">
        {tabs.map((tab, index) => (
          <Link
            to={tab.to}
            external={tab.external}
            className={tab.className}
            key={index}
          >
            {tab.label}
          </Link>
        ))}
      </div>
    );
  }

  renderSmall() {
    const classes = ['small'];
    if (this.props.helloBar) {
      classes.push('with-hello-bar');
    }
    const headerClasses = classes.join(' ');

    const {link, external} = this._getLogoLinkData();

    return (
      <div id="header-container" className={headerClasses}>

        <header aria-label="Main menu">
          {this._getHelloBar()}
          <div className="header-middle">
            <div className="header-left">
              {this._getMobileMenu()}
            </div>

            <div className="header-center">
              <div className="header-logo-container">
                <Link to={link} external={external}>
                  <img
                    src={imgMembershipLogoWhite}
                    alt="NatureBox"
                    className="header-logo"
                  />
                </Link>
              </div>
            </div>

            <div className="header-right">
              {this._getCart()}
            </div>
          </div>
        </header>

        <div className="header-padding">
        </div>
      </div>
    );
  }

  renderLarge() {
    const isOffice = this._getIsOfficeRoute();
    const classes = [];
    if (this.props.helloBar) {
      classes.push('with-hello-bar');
    }
    if (isOffice) {
      classes.push('office-route');
    }
    const headerClasses = classes.join(' ');

    const {link, external} = this._getLogoLinkData();

    return (
      <div id="header-container" className={headerClasses}>
        <header aria-label="Main menu">

          <div className="header-top">
            {/* This div has no content but creates
                symmetry with the right side tabs so that
                logo is centered correctly in flex container
            */}
            <div className="header-top-left"></div>
            <div className="header-top-center">
              <div className="header-logo-container">
                <Link to={link} external={external}>
                  <img
                    src={imgMembershipLogo}
                    alt="NatureBox"
                    className="header-logo"
                  />
                </Link>
              </div>
            </div>
            <div className="header-top-right">
              {this.renderTabs()}
            </div>
          </div>

          <div className="header-middle">
            <div className="header-middle-inner">
              <div className="header-middle-left">
                {this.renderMiddleLeft()}
              </div>

              <div className="header-middle-right">
                {this.renderMiddleRight()}
              </div>
            </div>
          </div>

          {this._getHelloBar()}
        </header>

        <div className="header-padding">
        </div>
      </div>
    );
  }

  renderSimple() {
    const {link, external} = this._getLogoLinkData();
    return (
      <div id="header-container" className="simple">
        <header aria-label="Main menu">
          <div className="header-simple-inner">
            <Link to={link} external={external}>
              <img
                src={imgMembershipLogo}
                alt="NatureBox"
                className="header-logo"
              />
            </Link>
          </div>
        </header>
        <div className="header-padding"></div>
      </div>
    );
  }

  render() {
    if (this.props.simpleHeader) {
      return this.renderSimple();
    }
    return (
      <React.Fragment>
        <MediaQuery query={Query.toEm({ max: 1100 })}>
          {this.renderSmall()}
        </MediaQuery>
        <MediaQuery query={Query.toEm({ min: 1101 })}>
          {this.renderLarge()}
        </MediaQuery>
      </React.Fragment>
    );
  }
}

export default withRouter(Header);
